// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// BackgroundEffect blurs the camera background CLIENT-SIDE — so it stays E2E: the
// homeserver/peer only ever see the already-composited frames. It runs MediaPipe selfie
// segmentation on each camera frame, composites the sharp person over a blurred copy of
// the frame onto a <canvas>, and exposes the canvas as a MediaStream
// (canvas.captureStream) whose video replaces the raw camera in the call. The WASM + model
// are SELF-HOSTED (/mediapipe/...) — never a CDN (blocked in censored regions).

import type { ImageSegmenter, ImageSegmenterResult } from "@mediapipe/tasks-vision";

export type BgMode = "none" | "blur";

export class BackgroundEffect {
  private segmenter: ImageSegmenter | null = null;
  private readonly video = document.createElement("video");
  private readonly canvas = document.createElement("canvas"); // output (w×h)
  private readonly matte = document.createElement("canvas"); // full-res masked person (w×h)
  private readonly small = document.createElement("canvas"); // raw mask at model res
  private ctx: CanvasRenderingContext2D | null = null;
  private mctx: CanvasRenderingContext2D | null = null;
  private sctx: CanvasRenderingContext2D | null = null;
  private raf = 0;
  private running = false;
  private mode: BgMode = "blur";
  private output: MediaStream | null = null;

  private async init(): Promise<void> {
    if (this.segmenter) return;
    // Dynamic import: keeps MediaPipe's ~300 KB JS out of the main bundle (and off the
    // wire entirely) until someone actually turns on the background effect.
    const vision = await import("@mediapipe/tasks-vision");
    const fileset = await vision.FilesetResolver.forVisionTasks("/mediapipe/wasm");
    this.segmenter = await vision.ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: "/mediapipe/selfie_segmenter.tflite", delegate: "GPU" },
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });
  }

  setMode(mode: BgMode): void {
    this.mode = mode;
  }

  // setInput swaps the SOURCE camera (a device switch) WITHOUT recreating the output — the
  // canvas/captureStream track stays the same, so the call keeps sending the processed
  // video and the blur persists across the switch.
  async setInput(stream: MediaStream): Promise<void> {
    this.video.srcObject = stream;
    await this.video.play().catch(() => undefined);
  }

  // start runs the effect over `input`'s camera track and returns a new stream (processed
  // video + the original audio passed through). Throws if the model can't load.
  async start(input: MediaStream, mode: BgMode): Promise<MediaStream> {
    this.mode = mode;
    await this.init();
    const track = input.getVideoTracks()[0];
    if (!track) throw new Error("no-video");
    const s = track.getSettings();
    const w = s.width ?? 640;
    const h = s.height ?? 480;
    this.canvas.width = this.matte.width = w;
    this.canvas.height = this.matte.height = h;
    this.ctx = this.canvas.getContext("2d");
    this.mctx = this.matte.getContext("2d");
    this.sctx = this.small.getContext("2d", { willReadFrequently: true });
    this.video.srcObject = new MediaStream([track]);
    this.video.muted = true;
    this.video.playsInline = true;
    await this.video.play();
    this.running = true;
    this.loop();
    const out = this.canvas.captureStream(24);
    for (const a of input.getAudioTracks()) out.addTrack(a); // audio is untouched
    this.output = out;
    return out;
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.output?.getVideoTracks().forEach((t) => t.stop());
    this.output = null;
    this.video.srcObject = null;
  }

  // dispose tears the effect down for good: stop the loop AND close the MediaPipe
  // segmenter, which owns a WASM graph + a WebGL2 context + GPU textures the JS GC
  // does NOT reclaim. Call this on call teardown (cleanup/hangup) — NOT on a blur-off
  // toggle, where stop() keeps the segmenter alive for reuse.
  dispose(): void {
    this.stop();
    this.segmenter?.close();
    this.segmenter = null;
  }

  private readonly loop = (): void => {
    if (!this.running) return;
    if (this.segmenter && this.video.readyState >= 2) {
      this.segmenter.segmentForVideo(this.video, performance.now(), (r) => this.composite(r));
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  private composite(result: ImageSegmenterResult): void {
    const ctx = this.ctx;
    const mctx = this.mctx;
    const sctx = this.sctx;
    const cat = result.categoryMask;
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (!ctx || !mctx || !sctx || !cat) {
      ctx?.drawImage(this.video, 0, 0, w, h);
      result.close();
      return;
    }
    if (this.mode === "none") {
      ctx.drawImage(this.video, 0, 0, w, h);
      result.close();
      return;
    }
    // Raw matte at the model resolution: alpha = person. The selfie_segmenter labels the
    // PERSON as category 0, so the matte keeps category-0 pixels (person sharp) and lets the
    // blurred background show through the rest.
    const mw = cat.width;
    const mh = cat.height;
    const data = cat.getAsUint8Array();
    this.small.width = mw;
    this.small.height = mh;
    const img = sctx.createImageData(mw, mh);
    for (let i = 0; i < data.length; i++) img.data[i * 4 + 3] = data[i] === 0 ? 255 : 0;
    sctx.putImageData(img, 0, 0);

    // Full-res masked person: scale the matte up, then keep the SHARP video only inside it.
    mctx.globalCompositeOperation = "copy";
    mctx.imageSmoothingEnabled = true;
    mctx.drawImage(this.small, 0, 0, w, h);
    mctx.globalCompositeOperation = "source-in";
    mctx.drawImage(this.video, 0, 0, w, h);
    mctx.globalCompositeOperation = "source-over";

    // Output = blurred background + the sharp person over it.
    ctx.save();
    ctx.filter = "blur(10px)";
    ctx.drawImage(this.video, 0, 0, w, h);
    ctx.restore();
    ctx.drawImage(this.matte, 0, 0, w, h);
    result.close();
  }
}
